<script>
import { createNamespacedHelpers } from 'vuex';

const { mapState, mapGetters, mapMutations } = createNamespacedHelpers('hitch');

export default {
  name: 'InputText',
  props: {
    fieldId: {
      type: String,
      default: 'primaryInput'
    }
  },
  data() {
    return {
      error: false
    }
  },
  computed: {
    ...mapState([
      'userText'
    ]),
    letterString: {
      set(string) {
        const processedString = this.processInput(string);
        this.setUserText(processedString);
      },
      get() {
        return this.userText;
      }
    },
    ...mapGetters([
      'getSmallLetterStyle',
      'getLargeLetterStyle',
      'getCustomizationStyle'
    ]),
    /** Returns placeholder text array depending on the customization style */
    placeholder() {
      return this.getCustomizationStyle.value === 'monogram' ? 'AAA' : 'AA';
    },
    /** Returns the letter limit depending on the customization style */
    letterLimit() {
      return this.getCustomizationStyle.value === 'monogram' ? 3 : 2;
    },
    /** Used on the maxlength param of the input */
    inputLetterLimit() {
      return this.error ? this.letterLimit : (this.letterLimit + 1);
    }
  },
  watch: {
    /**
     * Watch computed prop userText to dynamically bundle
     * letters and finally merge into Vuex
     */
    userText(letter) {
      let product = null;
      const bundleLetterArray = [];
      if(letter !== '') {
          let letterArray = letter.split('');
          letterArray.forEach((char, index) => {
            if(this.getCustomizationStyle.value === 'monogram' && (index === 0 || index === 2)){
              product = this.getProductByChar(char, this.getSmallLetterStyle.chars);
            }
            else {
              product = this.getProductByChar(char, this.getLargeLetterStyle.chars);
            }
            if(bundleLetterArray.length > 0 && bundleLetterArray.find(item => item.purchasableId === product.id)) {
              bundleLetterArray.forEach(item => {
                if(item.purchasableId === product.id) {
                  item.qty++;
                }
              });
            }
            else {
              bundleLetterArray.push({
                purchasableId: product.id,
                qty: 1,
                price: product.price > product.salePrice ? product.salePrice : product.price,
              });
            }
          });
      }
      // Set bundled letter products in Vuex 
      this.setBundleLetterProducts(bundleLetterArray);
    }
  },
  methods: {
    ...mapMutations([
      'setUserText',
      'setBundleLetterProducts'
    ]),
    /**
     * Method called from watcher that is triggered as a user types to confirm
     * the letter product exists, and updates the store values to keep track of the
     * products to include in the bundle
     * */
    processInput(text) {
      let textArray = text.split('');
      let char = '';
      let letterStyleChars = null;
      let productChar = null;
      let validString = '';

      // If this is monogram style and the user has typed in the first or the third letter
      // we need to get the small letter, otherwise we get the large letter
      if (
        this.getCustomizationStyle.value === 'monogram' &&
        (text.length === 1 || text.length === 3)
      ) {
        letterStyleChars = this.getSmallLetterStyle.chars;
      } else {
        letterStyleChars = this.getLargeLetterStyle.chars;
      }

      // Check if the current letter style has lowercase letters,
      // if not we reset the text array characters to all uppercase
      if (!this.hasLowercase(letterStyleChars)) {
        textArray = text.toUpperCase().split('');
      }

      // Get the last character input (after we have converted the text array or not to uppercase)
      char = textArray[textArray.length - 1];

      // Get the product for the letter and ensure that it exists, else remove it from the string
      productChar = this.getProductByChar(char, letterStyleChars);
      if (!productChar) {
        textArray.pop();
      }

      // If the text is too long for the style remove the last character and display the error warning
      if (textArray.length > this.letterLimit) {
        textArray.pop();
        this.error = true;
      } else {
        this.error = false;
      }

      // Get the final string from the text array to update the local letterString value
      // and set it in the Vuex store
      textArray.forEach((char) => {
        validString += char;
      });

      return validString;
    },
    /** Fetches a letter product based on a character */
    getProductByChar(char, array) {
      let product = null;
      const charProduct = array.filter((l) => l.key === char);
      if (charProduct.length) {
        product = charProduct[0];
      }
      return product;
    },
    /** Determines if a letter style has any lowercase letters */
    hasLowercase(letterStyle) {
      return letterStyle.filter((l) => l.type === 'lower').length > 0;
    }
  }
};
</script>

<template>
  <div>
    <input
      :id="fieldId"
      v-model="letterString"
      type="text"
      name="letter-text"
      :class="error ? 'error' : ''"
      :placeholder="placeholder"
      :maxlength="inputLetterLimit"
      @blur="error = false"
    />
    <span v-if="error" data-tip="Character limit reached for this customization style." />
  </div>
</template>

<style scoped>
  [data-tip]:after {
    display: block !important;
  }
  [data-tip]:before {
    display: block !important;
  }
  .error {
    border-color: #c03 !important;
  }
</style>
